/**
 * Product page behaviour.
 *
 * The one idea behind this file: the browser never computes variant
 * availability. When a shopper changes an option, the selected
 * `product_option_value` ids are sent back to the same section through the
 * Section Rendering API and Shopify returns markup that already knows which
 * combinations exist. Nothing here holds a variant list, so a product with 6
 * variants and one with 3000 cost the client exactly the same.
 *
 * Four custom elements:
 *   <product-info>             owns selection, fetching, the form and the URL
 *   <product-gallery>          owns media selection
 *   <product-lightbox>         owns the full-screen photo viewer, for the page
 *   <variant-swatch-carousel>  owns only horizontal swatch navigation
 *
 * The viewer serves the whole page rather than the gallery alone. Its triggers
 * live in the gallery and in blocks that script re-parents, so *opening* is
 * delegated from the document; its own chrome is bound to its dialog, where
 * nothing can come between a button and its handler.
 *
 * They re-initialise for free after a theme-editor section reload: replaced
 * markup upgrades into new element instances, and the listeners live on the
 * elements themselves rather than on the document, so nothing accumulates.
 */
(function () {
  // Everything that depends on which variant is selected. Each is swapped for
  // the freshly rendered server markup, index-wise, so a page holding more than
  // one of something updates all of them rather than just the first.
  const PARTS = [
    '[data-pdp-gallery-state]',
    '[data-pdp-picker]',
    '[data-pdp-price]',
    '[data-pdp-inventory]',
    '[data-pdp-sku]',
    '[data-pdp-pickup]',
    '[data-pdp-yarn-calculator-rules]',
    '[data-pdp-buy]'
  ];

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Matches the width the section collapses its columns at, so "mobile" means the
  // same thing in the stylesheet and here.
  const MOBILE = window.matchMedia('(max-width: 989px)');

  // Hover zoom needs a cursor to follow, which is a question about the input
  // device rather than the window. A wide touch screen must not get it. Same
  // query as the stylesheet uses.
  const FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');

  // How far a pointer may travel between press and release and still count as a
  // tap. The gallery is a swipeable track whose slides are buttons, so without
  // this a flick between photos ends in a click on whatever the finger left.
  const TAP_SLOP = 10;

  /**
   * A native horizontal scroller with optional buttons. CSS lays long swatch
   * sets into one or two rows; this element only moves that inline track and
   * updates its controls. It deliberately has no wheel or touch handler, so a
   * vertical gesture over the picker always belongs to the product page.
   */
  class VariantSwatchCarousel extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.track = this.querySelector('[data-pdp-carousel-track]');
      this.controls = this.querySelector('[data-pdp-carousel-controls]');
      this.previous = this.querySelector('[data-pdp-carousel-previous]');
      this.next = this.querySelector('[data-pdp-carousel-next]');
      if (!this.track) return;

      this.onScroll = this.scheduleUpdate.bind(this);
      this.onResize = this.scheduleUpdate.bind(this);
      this.onMediaLoad = this.scheduleUpdate.bind(this);
      this.onPrevious = () => this.move(-1);
      this.onNext = () => this.move(1);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('load', this.onMediaLoad, true);
      this.previous?.addEventListener('click', this.onPrevious);
      this.next?.addEventListener('click', this.onNext);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.onResize);
      }

      requestAnimationFrame(() => {
        this.revealSelected('auto');
        this.update();
      });
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('load', this.onMediaLoad, true);
      this.previous?.removeEventListener('click', this.onPrevious);
      this.next?.removeEventListener('click', this.onNext);
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.onResize);
      if (this.updateFrame) cancelAnimationFrame(this.updateFrame);
    }

    visibleItems() {
      return Array.from(this.querySelectorAll('.pdp-option__item:not([hidden])'));
    }

    scheduleUpdate() {
      if (this.updateFrame) return;
      this.updateFrame = requestAnimationFrame(() => {
        this.updateFrame = null;
        this.update();
      });
    }

    edgeState() {
      if (!this.track) return { overflow: false, before: false, after: false };

      const trackBox = this.track.getBoundingClientRect();
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const tolerance = 2;
      let before = false;
      let after = false;

      this.visibleItems().forEach((item) => {
        const box = item.getBoundingClientRect();
        if (rtl) {
          if (box.right > trackBox.right + tolerance) before = true;
          if (box.left < trackBox.left - tolerance) after = true;
        } else {
          if (box.left < trackBox.left - tolerance) before = true;
          if (box.right > trackBox.right + tolerance) after = true;
        }
      });

      return {
        overflow: this.track.scrollWidth > this.track.clientWidth + tolerance,
        before,
        after
      };
    }

    update() {
      const state = this.edgeState();
      if (this.controls) this.controls.hidden = !state.overflow;
      if (this.previous) this.previous.disabled = !state.before;
      if (this.next) this.next.disabled = !state.after;
    }

    move(direction) {
      if (!this.track) return;
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const styles = getComputedStyle(this.track);
      const columnGap = parseFloat(styles.columnGap) || 0;
      const pageWidth = Math.max(1, this.track.clientWidth - columnGap);
      const left = pageWidth * direction * (rtl ? -1 : 1);

      this.track.scrollBy({
        left,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    revealSelected(behavior = 'auto') {
      if (!this.track) return;
      const selected = this.querySelector('input[type="radio"]:checked')?.closest('.pdp-option__item');
      if (!selected || selected.hidden) return;

      const trackBox = this.track.getBoundingClientRect();
      const box = selected.getBoundingClientRect();
      let left = 0;
      if (box.left < trackBox.left) left = box.left - trackBox.left;
      if (box.right > trackBox.right) left = box.right - trackBox.right;
      if (Math.abs(left) > 1) this.track.scrollBy({ left, behavior });
    }

    resetToStart() {
      if (!this.track) return;
      const first = this.visibleItems()[0];
      if (!first) return;

      const trackBox = this.track.getBoundingClientRect();
      const box = first.getBoundingClientRect();
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const left = rtl ? box.right - trackBox.right : box.left - trackBox.left;
      if (Math.abs(left) > 1) this.track.scrollBy({ left, behavior: 'auto' });
    }

    refresh(reset = false) {
      requestAnimationFrame(() => {
        if (reset) this.resetToStart();
        this.update();
      });
    }

    get scrollPosition() {
      return this.track ? this.track.scrollLeft : 0;
    }

    restorePosition(position) {
      if (!this.track || typeof position !== 'number') return;
      this.track.scrollLeft = position;
      requestAnimationFrame(() => {
        this.revealSelected('auto');
        this.update();
      });
    }
  }

  // A dialog that was open when the shopper navigated away comes back open: the
  // back/forward cache restores the document as it stood, `open` attribute and
  // all, and a restored modal is no longer in the top layer. Closing it is the
  // only thing that state is good for.
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('[data-pdp-zoom][open]').forEach((dialog) => dialog.close());
  });

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.stage = this.querySelector('[data-pdp-gallery-stage]');
      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);

      // Filter and position before observing. Otherwise the first common image
      // at scroll position 0 can overwrite a deep-linked variant's media.
      this.syncVariantMedia(this.dataset.selectedMediaId, 'auto');
      this.watchHoverZoom();
    }

    /**
     * Points the magnified area at the cursor.
     *
     * Only the transform origin is managed here — the scale itself is a CSS
     * :hover rule. That means there is nothing to undo when the pointer leaves:
     * the scale returns to 1 on its own and a stale origin has no visible effect,
     * so the image also shrinks back toward wherever the cursor last was.
     *
     * Writes are coalesced into one animation frame, because pointermove fires far
     * more often than the screen refreshes.
     */
    watchHoverZoom() {
      if (!FINE_POINTER.matches) return;
      if (!this.querySelector('[data-pdp-zoom-hover]')) return;

      this.onPointerMove = this.onPointerMove.bind(this);
      this.addEventListener('pointermove', this.onPointerMove);
    }

    onPointerMove(event) {
      const frame = event.target.closest('[data-pdp-zoom-hover]');
      if (!frame) return;

      this.pendingZoom = { frame, x: event.clientX, y: event.clientY };
      if (this.zoomFrameQueued) return;

      this.zoomFrameQueued = true;
      requestAnimationFrame(() => {
        this.zoomFrameQueued = false;
        const pending = this.pendingZoom;
        if (!pending) return;

        const box = pending.frame.getBoundingClientRect();
        if (!box.width || !box.height) return;

        const x = ((pending.x - box.left) / box.width) * 100;
        const y = ((pending.y - box.top) / box.height) * 100;
        pending.frame.style.setProperty('--pdp-zoom-x', `${x.toFixed(2)}%`);
        pending.frame.style.setProperty('--pdp-zoom-y', `${y.toFixed(2)}%`);

        this.upgradeResolution(pending.frame);
      });
    }

    /**
     * Swaps in the large file the first time someone actually magnifies an image.
     * The page is served up to 1600px wide, which goes soft at 2x, but paying for
     * 2400px on every visit — including the visitors who never hover — is worse.
     * srcset has to go with it, or the browser keeps choosing from the old set.
     */
    upgradeResolution(frame) {
      const source = frame.dataset.hires;
      if (!source || frame.dataset.hiresApplied === 'true') return;

      const image = frame.querySelector('img');
      if (!image) return;

      frame.dataset.hiresApplied = 'true';
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = source;
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    /** Selecting a thumbnail is all this element owns on click. Opening a photo
     *  belongs to `product-lightbox`, which listens for its own triggers. */
    onClick(event) {
      const thumb = event.target.closest('[data-pdp-thumb]');
      if (thumb) this.select(thumb.dataset.mediaId);
    }

    /** Keeps the thumbnail strip honest while the shopper swipes the stage. */
    watchScroll() {
      if (!this.stage || !('IntersectionObserver' in window)) return;
      if (this.observer) this.observer.disconnect();
      // In grid and stacked layouts every media is on screen at once, so there is
      // no "current" one to track and the observer would just fight itself.
      if (this.dataset.galleryStyle === 'grid') return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const id = entry.target.dataset.mediaId;
            this.dataset.selectedMediaId = id;
            this.markThumb(id);
          });
        },
        { root: this.stage, threshold: 0.6 }
      );

      this.querySelectorAll('[data-pdp-media]:not([hidden])').forEach((item) =>
        this.observer.observe(item)
      );
    }

    markThumb(mediaId) {
      this.querySelectorAll('[data-pdp-thumb]').forEach((thumb) => {
        if (thumb.dataset.mediaId === mediaId) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    }

    /** Opens whatever media Liquid marked as selected, without animating. */
    openSelected() {
      const item = this.mediaById(this.dataset.selectedMediaId);
      if (!item) return;
      this.markThumb(this.dataset.selectedMediaId);
      this.scrollToItem(item, 'auto');
    }

    mediaById(mediaId) {
      if (!mediaId) return null;
      return this.querySelector(`[data-pdp-media][data-media-id="${mediaId}"]`);
    }

    firstVisibleMedia() {
      return this.querySelector('[data-pdp-media]:not([hidden])');
    }

    /**
     * Keeps the selected colour group plus every ungrouped, unlinked product
     * media. Native variant-media ids remain a fallback for products that only
     * use Shopify's variant attachment and don't use `color-*` alt groups.
     */
    syncVariantMedia(mediaId, behavior = 'smooth') {
      if (this.observer) this.observer.disconnect();

      const state = this.querySelector('[data-pdp-gallery-state]');
      const selectedId = String(
        mediaId || (state && state.dataset.selectedMediaId) || this.dataset.selectedMediaId || ''
      );
      const filterEnabled = Boolean(state && state.dataset.filterVariantMedia === 'true');
      const selectedGroup = (state && state.dataset.selectedMediaGroup) || '';
      const variantIds = new Set(
        ((state && state.dataset.variantMediaIds) || '').split('|').filter(Boolean)
      );

      this.querySelectorAll('[data-pdp-media]').forEach((item) => {
        const mediaGroup = item.dataset.mediaGroup || '';
        const groupedElsewhere = mediaGroup && (!selectedGroup || mediaGroup !== selectedGroup);
        const attachedElsewhere =
          !mediaGroup && variantIds.has(item.dataset.mediaId) && item.dataset.mediaId !== selectedId;
        const hidden = filterEnabled && (groupedElsewhere || attachedElsewhere);
        item.hidden = hidden;
        if (hidden) item.querySelectorAll('video').forEach((video) => video.pause());
      });

      this.querySelectorAll('[data-pdp-thumb]').forEach((thumb) => {
        const mediaGroup = thumb.dataset.mediaGroup || '';
        const groupedElsewhere = mediaGroup && (!selectedGroup || mediaGroup !== selectedGroup);
        const attachedElsewhere =
          !mediaGroup && variantIds.has(thumb.dataset.mediaId) && thumb.dataset.mediaId !== selectedId;
        thumb.hidden = filterEnabled && (groupedElsewhere || attachedElsewhere);
      });

      let visibleItems = Array.from(this.querySelectorAll('[data-pdp-media]:not([hidden])'));

      // A variant with no media and a product with no shared media should still
      // have a usable gallery rather than an empty region.
      if (!visibleItems.length) {
        const fallback = this.querySelector('[data-pdp-media]');
        if (fallback) {
          fallback.hidden = false;
          const fallbackThumb = this.querySelector(
            `[data-pdp-thumb][data-media-id="${fallback.dataset.mediaId}"]`
          );
          if (fallbackThumb) fallbackThumb.hidden = false;
          visibleItems = [fallback];
        }
      }

      this.classList.toggle('pdp-gallery--single-visible', visibleItems.length === 1);
      this.updateThumbLabels();

      // The group's first photo wins, not the one Shopify has attached to the
      // variant.
      //
      // A shopper who picks a colour is asking for the colour, not for one
      // particular photograph of it, and they expect to land at the start of that
      // colour's set — the same place the thumbnail strip starts. The attached
      // media is whatever the merchant happened to link in the admin, which on
      // this store is the label shot on some colours and the texture shot on
      // others, so honouring it made the gallery look like it had skipped ahead.
      //
      // DOM order is `product.media` order, so `visibleItems[0]` is exactly "first
      // in the strip". The attached media stays as the fallback for the case where
      // the filter has hidden everything and the block below has had to force one
      // item back on.
      const selected = this.mediaById(selectedId);
      let target = visibleItems[0];
      if (!target && selected && !selected.hidden) target = selected;

      if (target) {
        this.dataset.selectedMediaId = target.dataset.mediaId;
        this.markThumb(target.dataset.mediaId);
        this.scrollToItem(target, behavior);
      }

      this.watchScroll();
    }

    updateThumbLabels() {
      const template = this.dataset.textViewMedia;
      if (!template) return;

      const visibleThumbs = Array.from(this.querySelectorAll('[data-pdp-thumb]:not([hidden])'));
      visibleThumbs.forEach((thumb, index) => {
        thumb.setAttribute(
          'aria-label',
          template.replace('__index__', String(index + 1)).replace('__total__', String(visibleThumbs.length))
        );
      });
    }

    /**
     * Scrolls the stage only, never the page — scrollIntoView would drag the
     * document when the gallery sits below the fold. The offset is measured
     * against the first item, which also makes it right in right-to-left
     * layouts: there the first item is the rightmost, so the difference comes
     * out negative, which is exactly what scrollLeft expects in RTL.
     */
    scrollToItem(item, behavior) {
      if (!this.stage) return;
      const first = this.firstVisibleMedia();
      if (!first) return;

      this.stage.scrollTo({
        left: item.offsetLeft - first.offsetLeft,
        behavior: prefersReducedMotion() ? 'auto' : behavior
      });
    }

    select(mediaId) {
      const item = this.mediaById(mediaId);
      if (!item) return;

      this.dataset.selectedMediaId = mediaId;
      this.markThumb(mediaId);
      this.scrollToItem(item, 'smooth');
    }
  }

  /**
   * The page's single full-screen photo viewer.
   *
   * One per product section, rendered outside the gallery, opened by anything
   * carrying `data-pdp-zoom-open` — the gallery's photos, the info links block's
   * colour chart and specification sheet. Before this existed the gallery and the
   * info links each had a viewer of their own, and which one a shopper met
   * depended on which picture they tapped.
   *
   * Clicks are delegated from the document rather than from an ancestor. The
   * triggers are scattered across blocks, and the info links block is re-parented
   * under the gallery by script after render, so nothing scoped to a subtree
   * survives. The listeners come off again on disconnect.
   */
  class ProductLightbox extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-pdp-zoom]');
      if (!this.dialog) return;

      if (!this.bound) {
        this.bound = true;
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDialogClick = this.onDialogClick.bind(this);
        this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
        this.onZoomClose = this.onZoomClose.bind(this);
      }

      // Two listeners, deliberately, because they answer two different questions.
      //
      // The document one is only about OPENING. Triggers are scattered across the
      // gallery and the blocks, and one of those blocks is re-parented under the
      // gallery by script after render, so nothing scoped to a subtree can catch
      // them all.
      //
      // The viewer's own chrome — close, arrows, thumbnails, dismissal — hangs off
      // the dialog instead. Everything inside it is unambiguously ours, so there
      // is no ownership test to get wrong, and a press of the close button cannot
      // be lost somewhere on its way up to the document. Routing the chrome
      // through the document listener is what left every one of those controls
      // dead to the mouse.
      document.addEventListener('click', this.onDocumentClick);
      this.dialog.addEventListener('click', this.onDialogClick);
      // Tracked so a swipe across the gallery is not mistaken for a tap on it.
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
      // Teardown hangs off the dialog's own `close` event rather than off the
      // close button, because Escape and `close()` never pass through the button.
      // Everything that must be undone is undone in one place, once.
      this.dialog.addEventListener('close', this.onZoomClose);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      if (this.dialog) this.dialog.removeEventListener('click', this.onDialogClick);
      // A section swapped out with the viewer open would otherwise leave the page
      // locked and a Swiper instance listening to a detached tree.
      this.teardownZoom();
    }

    /**
     * True when the pointer travelled far enough that this was a drag.
     *
     * Two places need it: opening the viewer from a swipeable gallery, and
     * dismissing it by clicking the empty area — a swipe across a slide also ends
     * in a click on that slide, and dismissing mid-swipe is the worse failure.
     *
     * Anything unmeasurable counts as a genuine activation. Being wrong that way
     * opens a viewer the shopper can close; being wrong the other way makes the
     * photo look broken.
     */
    wasDrag(event, press, trigger) {
      // A keyboard activation reports no detail and no coordinates.
      if (!event.detail) return false;
      if (!press) return false;
      if (trigger && press.target !== trigger && !trigger.contains(press.target)) return false;
      return Math.hypot(event.clientX - press.x, event.clientY - press.y) > TAP_SLOP;
    }

    onDocumentPointerDown(event) {
      this.pointerStart = { x: event.clientX, y: event.clientY, target: event.target };
    }

    /** Opening only. Everything inside the viewer is `onDialogClick`'s. */
    onDocumentClick(event) {
      // Consumed once per click, whatever the click turns out to be, so a press
      // that began on one thing is never measured against a later click on
      // another. The dialog's own handler runs first and takes it for clicks in
      // there, which is fine: those are never triggers.
      const press = this.pointerStart;
      this.pointerStart = null;

      const trigger = event.target.closest('[data-pdp-zoom-open]');
      if (!trigger) return;
      if (!this.owns(trigger)) return;
      if (this.wasDrag(event, press, trigger)) return;
      this.openZoom(trigger);
    }

    /** The viewer's own chrome. Bound to the dialog, so nothing can intervene. */
    onDialogClick(event) {
      const press = this.pointerStart;
      this.pointerStart = null;

      // Belt and braces: the close button is a submit inside `form
      // method="dialog"`, so the browser has already closed the dialog by the
      // time this runs. Kept for the case where that button is ever moved out of
      // the form.
      if (event.target.closest('[data-pdp-zoom-close]')) {
        this.closeZoom();
        return;
      }

      const zoomThumb = event.target.closest('[data-pdp-zoom-thumb]');
      if (zoomThumb) {
        this.goToZoom(Number(zoomThumb.dataset.pdpZoomThumb));
        return;
      }

      // The arrows are ours in every case, Swiper or not. Pressing one at either
      // end is a no-op rather than a disabled control, so it swallows its own
      // click instead of letting it fall through to the slide behind.
      if (event.target.closest('[data-pdp-zoom-prev]')) {
        this.goToZoom(this.zoomIndex - 1);
        return;
      }
      if (event.target.closest('[data-pdp-zoom-next]')) {
        this.goToZoom(this.zoomIndex + 1);
        return;
      }

      this.maybeDismissZoom(event, press);
    }

    /**
     * "Click the empty area to close."
     *
     * The dialog fills the screen and the Swiper fills the dialog, so the space
     * around the photo belongs to one of these elements rather than to the dialog
     * itself — the slide's own padding is most of what a shopper actually aims at.
     * Anything else in there is a control and keeps its own click.
     */
    maybeDismissZoom(event, press) {
      const dialog = event.target.closest('[data-pdp-zoom]');
      if (!dialog || dialog.dataset.backdropClose !== 'true') return;

      // A swipe between photos, not a dismissal.
      if (this.wasDrag(event, press, null)) return;

      // Magnified: this click ended a pan, or is a double-click on its way to
      // toggling zoom back off. Either way it is not a request to leave.
      if (this.isZoomedIn()) return;

      const empty =
        event.target === dialog ||
        event.target.classList.contains('pdp-zoom__stage') ||
        event.target.classList.contains('pdp-zoom__swiper') ||
        event.target.classList.contains('swiper-wrapper') ||
        event.target.classList.contains('swiper-slide') ||
        event.target.classList.contains('swiper-zoom-container') ||
        event.target.classList.contains('pdp-zoom__footer');
      if (empty) this.closeZoom();
    }

    isZoomedIn() {
      if (!this.zoomSwiper || !this.zoomSwiper.zoom) return false;
      return this.zoomSwiper.zoom.scale > 1;
    }

    /**
     * The photos this set may move through: every visible trigger sharing the
     * clicked trigger's group.
     *
     * `[hidden]` is what keeps it honest when the gallery's variant filter has
     * hidden media — the viewer must never navigate to a photo the page has
     * decided doesn't belong to this variant. Expressed as "not inside anything
     * hidden" rather than as a gallery rule, so the viewer needs to know nothing
     * about variants, and a colour chart in its own group simply opens alone.
     */
    zoomTriggers(group) {
      return [...document.querySelectorAll('[data-pdp-zoom-open]')].filter(
        (el) => el.dataset.zoomGroup === group && !el.closest('[hidden]')
      );
    }

    /** Triggers belong to the section this viewer was rendered into. */
    owns(node) {
      const root = this.closest('product-info');
      if (!root) return true;
      if (root.contains(node)) return true;

      // Pattern detail tabs are a full-width section below Product main, so
      // they cannot be descendants of this viewer's `product-info`. They still
      // belong to the same product and deliberately hand that id over. This
      // keeps one lightbox per product page without letting an unrelated quick
      // view or featured product borrow it.
      return node.dataset.zoomProductId === root.dataset.productId;
    }

    openZoom(trigger) {
      const dialog = this.dialog;
      const track = this.querySelector('[data-pdp-zoom-track]');
      if (!dialog || !track || typeof dialog.showModal !== 'function') return;

      const triggers = this.zoomTriggers(trigger.dataset.zoomGroup);
      if (!triggers.length) return;
      const index = Math.max(0, triggers.indexOf(trigger));

      // A modal dialog that gets re-parented keeps its `open` attribute but loses
      // its place in the top layer, and a theme-editor section reload re-parents
      // things routinely. What is left renders in normal flow with no backdrop.
      // `showModal()` also throws outright on a dialog that is already open, so
      // closing first is the only way back to a real modal in either case.
      //
      // This has to happen BEFORE the slides are built: `close()` fires `close`
      // synchronously, and the teardown behind that event empties the track. Built
      // first, the slides would be wiped and the viewer would open blank.
      if (dialog.open) dialog.close();

      this.buildZoom(triggers, index);
      this.lockPage();
      dialog.showModal();

      // Swiper must be initialised against a laid-out container. A dialog is
      // `display: none` until it opens, and a Swiper built in that state measures
      // every slide as zero wide and never recovers. Hence: open, then build.
      this.startZoomSwiper(index);
    }

    /**
     * Builds the slides. Sources sit in data attributes until a slide is reached,
     * because a lazy image inside a dialog never loads on its own — the dialog is
     * `display: none` and then sits in the top layer, so the intersection that
     * would trigger the fetch never happens.
     */
    buildZoom(triggers, index) {
      const track = this.querySelector('[data-pdp-zoom-track]');
      const thumbs = this.querySelector('[data-pdp-zoom-thumbs]');
      const labelTemplate = this.dataset.textViewMedia || '';

      // Without the vendored Swiper there is nothing to drive a track, so the
      // viewer degrades to the one photo that was clicked. Full screen, closable,
      // Escape still works — just not navigable. Swiper is deferred, not async,
      // so this is the script-blocked case rather than a race.
      const hasSwiper = typeof window.Swiper === 'function';
      const set = hasSwiper ? triggers : [triggers[index]];

      track.replaceChildren(
        ...set.map((trigger) => {
          const slide = document.createElement('div');
          slide.className = 'swiper-slide pdp-zoom__slide';

          // A video arrives as the id of a `<template>` rather than as image
          // sources. Cloning it here is what keeps `video_tag` — and therefore
          // real sources and real controls — in Liquid, and a template's contents
          // stay unfetched until this line runs.
          //
          // No zoom container: pinching a video means nothing, and Swiper's zoom
          // would fight the controls for the same gestures. `swiper-no-swiping`
          // for the same reason at the other end — without it, dragging the scrub
          // bar turns into a slide change.
          const templateId = trigger.dataset.zoomTemplate;
          if (templateId) {
            const template = document.getElementById(templateId);
            const player = document.createElement('div');
            player.className = 'pdp-zoom__player swiper-no-swiping';
            if (template && template.content) player.appendChild(template.content.cloneNode(true));
            slide.appendChild(player);
            return slide;
          }

          // Swiper's zoom works on the contents of this wrapper, and its own
          // stylesheet centres and contains the image inside it. Letting Swiper
          // own the sizing is what keeps the photo from being stretched.
          const container = document.createElement('div');
          container.className = 'swiper-zoom-container';

          const image = document.createElement('img');
          image.className = 'pdp-zoom__image';
          image.alt = trigger.dataset.zoomAlt || '';
          image.dataset.src = trigger.dataset.zoomSrc;
          if (trigger.dataset.zoomSrcset) image.dataset.srcset = trigger.dataset.zoomSrcset;
          // The slide is the stage minus its padding. Close enough for the browser
          // to choose sensibly, and far better than always taking the 2400px file.
          image.sizes = '90vw';
          // Intrinsic size lets zoom stop at the file's own resolution instead of
          // magnifying into a blur, and reserves the box while it decodes.
          if (trigger.dataset.zoomWidth) image.width = Number(trigger.dataset.zoomWidth);
          if (trigger.dataset.zoomHeight) image.height = Number(trigger.dataset.zoomHeight);
          // Firefox starts a native image drag on mousedown, which cancels the pan
          // across a magnified photo. WebKit and Blink need the CSS counterpart.
          image.draggable = false;

          // Until the file arrives the viewer is an empty screen with nothing to
          // say for itself, which reads as broken rather than as busy.
          slide.classList.add('is-loading');
          const settle = () => slide.classList.remove('is-loading');
          image.addEventListener('load', settle, { once: true });
          image.addEventListener('error', settle, { once: true });

          container.appendChild(image);
          slide.appendChild(container);
          return slide;
        })
      );

      if (thumbs) {
        thumbs.replaceChildren(
          ...set.map((trigger, i) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'pdp-zoom__thumb';
            button.setAttribute('data-pdp-zoom-thumb', String(i));
            button.setAttribute(
              'aria-label',
              labelTemplate.replace('__index__', String(i + 1)).replace('__total__', String(set.length))
            );

            // A 160px file for a 60px button. This used to take the same 2400px
            // source as the slide, once per thumbnail. A video whose poster the
            // merchant never set has neither, and an `img` with an empty src is a
            // broken-image icon — so it simply gets none.
            const source = trigger.dataset.zoomThumb || trigger.dataset.zoomSrc;
            if (source) {
              const image = document.createElement('img');
              image.src = source;
              image.alt = '';
              image.loading = 'lazy';
              button.appendChild(image);
            }
            return button;
          })
        );
      }

      this.zoomCount = set.length;
      // The fallback set holds one photo, so the clicked index collapses to 0.
      this.zoomIndex = hasSwiper ? index : 0;
      this.hydrateZoom(this.zoomIndex);
    }

    startZoomSwiper(index) {
      const el = this.querySelector('[data-pdp-zoom-swiper]');
      const dialog = this.dialog;
      const prevEl = this.querySelector('[data-pdp-zoom-prev]');
      const nextEl = this.querySelector('[data-pdp-zoom-next]');

      if (!el || typeof window.Swiper !== 'function') {
        this.zoomSwiper = null;
        if (prevEl) prevEl.hidden = true;
        if (nextEl) nextEl.hidden = true;
        this.markZoomPosition(0, this.zoomCount);
        return;
      }

      const maxRatio = Number(dialog.dataset.maxRatio) || 3;
      const reduced = prefersReducedMotion();

      this.zoomSwiper = new window.Swiper(el, {
        initialSlide: index,
        speed: reduced ? 0 : 300,
        // The dialog is the whole screen and nothing scrolls behind it, so a
        // slide is only ever a slide — no need to watch for anything else.
        watchOverflow: true,
        // Pinch, double-tap and double-click all come from here, along with
        // panning inside a magnified photo and the lock that stops a pan turning
        // into a slide change. That lock is the part worth not hand-rolling.
        zoom: {
          maxRatio: maxRatio,
          minRatio: 1,
          toggle: true,
          // Stops magnification past the file's own pixels, which is what keeps
          // 3x from meaning "3x blurrier" on a photo the shopper already sees big.
          limitToOriginalSize: true
        },
        keyboard: { enabled: true, onlyInViewport: false },
        // No `navigation` block: the arrows are driven from this file instead.
        //
        // Swiper's navigation module does `if (el.tagName === 'BUTTON')
        // el.disabled = state`, and a natively disabled button is the one thing
        // this viewer must not have. It drops focus to the body the moment a
        // keyboard shopper arrows onto the last photo, and it stops hit-testing,
        // so a click aimed at the greyed-out arrow falls through to the slide
        // underneath — which "click the empty area to close" then treats as a
        // request to leave. Pressing a disabled arrow closed the viewer.
        //
        // Swiper keeps what it is good at: touch, pinch, pan and momentum.
        // Off on purpose, and it defaults to ON — so this line is load-bearing.
        // Swiper's a11y module overwrites the arrows' `aria-label` with its own
        // `prevSlideMessage` / `nextSlideMessage`, which are hardcoded English
        // ("Previous slide"). This theme translates everything through
        // `locales/*.json`, so the labels stay Liquid's. What the module would
        // have contributed is already covered: the arrows carry translated
        // labels, each photo carries real alt text, the counter is a polite live
        // region, and `aria-disabled` is maintained below.
        a11y: { enabled: false },
        on: {
          slideChange: (swiper) => {
            // Leaving a photo magnified would carry the scale onto the next one.
            if (swiper.zoom) swiper.zoom.out();
            // A video left running on the slide behind keeps its sound going.
            this.pauseZoomMedia();
            this.zoomIndex = swiper.activeIndex;
            this.hydrateZoom(swiper.activeIndex);
            this.markZoomPosition(swiper.activeIndex, swiper.slides.length);
          }
        }
      });

      this.markZoomPosition(index, this.zoomCount);

      // `showModal()` lays the dialog out synchronously, but a viewport unit that
      // resolves against a just-appeared top-layer box can still land a frame
      // late on mobile Safari. One update costs nothing and removes the class of
      // bug where the first slide is the wrong width.
      requestAnimationFrame(() => {
        if (this.zoomSwiper) this.zoomSwiper.update();
      });
    }

    /** Fetches the photo being shown and its immediate neighbours, nothing else. */
    hydrateZoom(index) {
      const slides = [...this.querySelectorAll('[data-pdp-zoom-track] .pdp-zoom__image')];
      [index - 1, index, index + 1].forEach((i) => {
        const image = slides[i];
        if (!image || !image.dataset.src) return;
        // srcset before src, so the browser makes its choice with the candidate
        // list already in hand rather than committing to the fallback first.
        if (image.dataset.srcset) {
          image.srcset = image.dataset.srcset;
          delete image.dataset.srcset;
        }
        image.src = image.dataset.src;
        delete image.dataset.src;
      });
    }

    goToZoom(index) {
      // Clamped here rather than guarded at every call site, so an arrow pressed
      // at either end simply lands back where it was. No wrapping: an arrow that
      // silently jumps from the last photo to the first reads as a bug when there
      // are only three.
      const clamped = Math.min(Math.max(index, 0), Math.max(0, this.zoomCount - 1));

      if (this.zoomSwiper) {
        this.zoomSwiper.slideTo(clamped);
        return;
      }
      // Fallback: one slide, so there is nowhere to go. Keep the counter honest.
      this.markZoomPosition(clamped, this.zoomCount);
    }

    markZoomPosition(index, total) {
      const counter = this.querySelector('[data-pdp-zoom-count]');
      if (counter && this.dialog) {
        const template = this.dialog.dataset.textCount || '';
        counter.textContent = template
          .replace('__index__', String(index + 1))
          .replace('__total__', String(total));
      }

      const thumbs = this.querySelector('[data-pdp-zoom-thumbs]');
      this.querySelectorAll('[data-pdp-zoom-thumb]').forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });

      // `aria-disabled`, never the `disabled` property. Disabling a focused button
      // drops focus to the body, so a keyboard shopper who arrows to the last
      // photo would be stranded — and a disabled button stops hit-testing, which
      // turns the arrow into a hole the click falls through. The button stays
      // real; `goToZoom` clamps, so pressing it at the end does nothing.
      //
      // One photo needs no arrows at all. Hidden here rather than through
      // Swiper's lock class, since Swiper no longer owns these buttons.
      const prev = this.querySelector('[data-pdp-zoom-prev]');
      const next = this.querySelector('[data-pdp-zoom-next]');
      const single = total < 2;
      // A colour chart, a specification sheet and a video all open alone. "1 / 1"
      // and a strip holding one thumbnail are noise about a choice that isn't
      // there, so the whole footer stands down.
      if (counter) counter.hidden = single;
      if (thumbs) thumbs.hidden = single;
      if (prev) {
        prev.setAttribute('aria-disabled', String(index === 0));
        prev.hidden = single;
      }
      if (next) {
        next.setAttribute('aria-disabled', String(index >= total - 1));
        next.hidden = single;
      }

      // Scrolled by hand rather than with scrollIntoView, which is allowed to
      // scroll ancestors — including the page behind the dialog.
      if (thumbs) {
        const active = thumbs.children[index];
        if (active) {
          thumbs.scrollTo({
            left: active.offsetLeft - (thumbs.clientWidth - active.clientWidth) / 2,
            behavior: prefersReducedMotion() ? 'auto' : 'smooth'
          });
        }
      }
    }

    pauseZoomMedia() {
      this.querySelectorAll('[data-pdp-zoom-track] video').forEach((video) => {
        if (!video.paused) video.pause();
      });
    }

    closeZoom() {
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    /** Runs for every way out: the button, Escape, `close()`, a form dismissal. */
    onZoomClose() {
      this.teardownZoom();
    }

    teardownZoom() {
      if (this.zoomSwiper) {
        this.zoomSwiper.destroy(true, true);
        this.zoomSwiper = null;
      }

      this.pauseZoomMedia();

      // Let the decoded bitmaps go. Reopening rebuilds from cache.
      const track = this.querySelector('[data-pdp-zoom-track]');
      if (track) track.replaceChildren();
      const thumbs = this.querySelector('[data-pdp-zoom-thumbs]');
      if (thumbs) thumbs.replaceChildren();

      this.unlockPage();
    }

    /**
     * A modal dialog makes the page inert but does not stop it scrolling, so on
     * touch the photo stays put while the product page slides around underneath.
     * Counted, because two galleries on one page must not unlock each other.
     */
    lockPage() {
      const root = document.documentElement;
      const open = Number(root.dataset.pdpZoomOpen || 0) + 1;
      root.dataset.pdpZoomOpen = String(open);
      root.classList.add('pdp-zoom-lock');
      this.pageLocked = true;
    }

    unlockPage() {
      if (!this.pageLocked) return;
      this.pageLocked = false;
      const root = document.documentElement;
      const open = Math.max(0, Number(root.dataset.pdpZoomOpen || 1) - 1);
      if (open === 0) {
        delete root.dataset.pdpZoomOpen;
        root.classList.remove('pdp-zoom-lock');
      } else {
        root.dataset.pdpZoomOpen = String(open);
      }
    }
  }

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.sectionId = this.dataset.sectionId;
      this.requestId = 0;

      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.placeBadges = this.placeBadges.bind(this);

      // Delegated, so blocks replaced on a variant change come back wired up and
      // no listener is ever added twice.
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopState);

      // Desktop and mobile placement are separate settings, so crossing the
      // breakpoint has to move the block.
      MOBILE.addEventListener('change', this.placeBadges);

      this.placeBadges();
      this.placeUnderGallery();
      this.placePurchaseActions();
      this.placeServiceHighlights();
      this.watchStickyColumn();
    }

    /**
     * Decides which column follows the shopper down, when the merchant left that
     * on `auto`.
     *
     * The shorter column is the one that should travel: the taller one still has
     * something to show, and beside it the shorter one would otherwise leave a
     * long strip of nothing. Which column that is cannot be known in Liquid — the
     * buy box grows with every block the merchant adds and the media column grows
     * with the photo count — so it is measured here.
     *
     * A `ResizeObserver` rather than a one-off measurement, because the heights
     * move: a variant change re-renders the buy box, blocks are re-parented into
     * the gallery after this runs, product tabs open and close, and fonts land
     * late. Observing both columns covers all of it without knowing about any of
     * it. Applying the class doesn't change either height, so this cannot feed
     * itself.
     */
    watchStickyColumn() {
      this.inner = this.querySelector('[data-pdp-inner]');
      this.mediaColumn = this.querySelector('[data-pdp-media-column]');
      this.infoColumn = this.querySelector('[data-pdp-info-column]');
      if (!this.inner || !this.mediaColumn || !this.infoColumn) return;
      if (this.inner.dataset.stickyColumn !== 'auto') return;

      this.syncStickyColumn = this.syncStickyColumn.bind(this);
      this.syncStickyColumn();

      if ('ResizeObserver' in window) {
        this.columnObserver = new ResizeObserver(this.syncStickyColumn);
        this.columnObserver.observe(this.mediaColumn);
        this.columnObserver.observe(this.infoColumn);
      }
      window.addEventListener('resize', this.syncStickyColumn);
    }

    syncStickyColumn() {
      const media = this.mediaColumn;
      const info = this.infoColumn;

      // One column: the two are stacked, so neither follows the other.
      const stacked = MOBILE.matches || !this.inner.classList.contains('pdp__inner--two_column');

      const mediaHeight = media.offsetHeight;
      const infoHeight = info.offsetHeight;

      // The offset matches the one the stylesheet uses for `top`/`bottom`.
      const offset = parseFloat(getComputedStyle(this.inner).getPropertyValue('--spacing-lg')) || 24;
      const room = window.innerHeight - offset * 2;

      // Nearly equal heights have nothing to gain and would flip back and forth on
      // a few pixels of reflow, so there is a deliberate dead zone.
      const difference = Math.abs(mediaHeight - infoHeight);

      let follower = null;
      let pinEnd = false;
      if (!stacked && difference > 100) {
        follower = mediaHeight < infoHeight ? media : info;

        // Which edge gets pinned depends on whether the follower fits the screen.
        //
        // Pinning the top of something taller than the viewport freezes it with
        // its own bottom permanently out of reach — the shopper can never read the
        // end of it. Pinning the bottom instead lets it scroll up with the page
        // until its last line is on screen and only then holds, so all of it is
        // reachable and it still stops leaving a gap beside the taller column.
        //
        // This is the case that matters here rather than an edge case: at a
        // 1200px content width the media column is around 624px wide, so a square
        // photo plus its thumbnail strip plus the blocks moved underneath it comes
        // to more than most laptop screens. Refusing to stick at all in that
        // situation is what made `auto` look broken.
        pinEnd = follower.offsetHeight > room;
      }

      const wanted = follower === media ? 'media' : follower === info ? 'info' : 'none';
      const state = wanted + (pinEnd ? ':end' : '');
      if (this.stickyApplied === state) return;
      this.stickyApplied = state;

      media.classList.toggle('pdp__media--sticky', follower === media);
      info.classList.toggle('pdp__info--sticky', follower === info);
      media.classList.toggle('is-sticky-end', follower === media && pinEnd);
      info.classList.toggle('is-sticky-end', follower === info && pinEnd);
    }

    /**
     * Moves any block that asks for the media column under the gallery — the info
     * links row does. Same reason the badges move: the gallery is rendered before
     * blocks are read, so Liquid can't put anything there, and a section of its
     * own would be a sibling on the page rather than inside the media column.
     *
     * Unlike the badges this has no breakpoint variant: under the thumbnails is
     * under the thumbnails at every width.
     */
    placeUnderGallery() {
      const gallery = this.gallery;
      if (!gallery) return;

      this.querySelectorAll('[data-pdp-gallery-slot="under"]').forEach((el) => {
        const node = el.closest('.shopify-block') || el;
        if (node.parentElement === gallery) return;
        gallery.appendChild(node);
      });
    }

    /**
     * Groups the quantity and buy-button theme blocks into one purchase grid.
     * The wrappers themselves move, rather than copies of their contents, so the
     * theme editor keeps its block handles and the quantity input keeps pointing
     * at the original product form. The group sits outside `[data-pdp-buy]`, which
     * means a variant refresh can replace that block without deleting quantity.
     */
    placePurchaseActions() {
      const infoColumn = this.querySelector('[data-pdp-info-column]');
      const quantity = infoColumn?.querySelector('.pdp-quantity');
      const buy = infoColumn?.querySelector('[data-pdp-buy]');
      if (!infoColumn || !quantity || !buy) return;

      const quantityNode = quantity.closest('.shopify-block') || quantity;
      const buyNode = buy.closest('.shopify-block') || buy;
      if (quantityNode === buyNode) return;

      let group = infoColumn.querySelector('[data-pdp-purchase-actions]');
      if (!group) {
        group = document.createElement('div');
        group.className = 'pdp-purchase-actions';
        group.dataset.pdpPurchaseActions = '';

        const infoChildren = Array.from(infoColumn.children);
        const quantityIndex = infoChildren.indexOf(quantityNode);
        const buyIndex = infoChildren.indexOf(buyNode);
        const firstNode = quantityIndex <= buyIndex ? quantityNode : buyNode;
        infoColumn.insertBefore(group, firstNode);
      }

      if (quantityNode.parentElement !== group) group.appendChild(quantityNode);
      if (buyNode.parentElement !== group) group.appendChild(buyNode);
    }

    /**
     * Service highlights can follow either the media-column info links or the
     * buy buttons in the product-information column. The Shopify wrapper moves
     * with the block so editor selection and drag handles remain intact.
     */
    placeServiceHighlights() {
      const highlights = Array.from(
        this.querySelectorAll('[data-pdp-service-highlights][data-placement]')
      );
      if (!highlights.length) return;

      const infoLinks = this.querySelector('[data-pdp-info-links]');
      let infoCursor = infoLinks && (infoLinks.closest('.shopify-block') || infoLinks);
      const gallery = this.gallery;

      const buy = this.querySelector('[data-pdp-buy]');
      const purchaseActions = buy?.closest('[data-pdp-purchase-actions]');
      let buyCursor = purchaseActions || (buy && (buy.closest('.shopify-block') || buy));
      const infoColumn = this.querySelector('.pdp__info');

      highlights.forEach((highlight) => {
        const node = highlight.closest('.shopify-block') || highlight;

        if (highlight.dataset.placement === 'under_info_links') {
          if (infoCursor && infoCursor.parentElement) {
            if (infoCursor.nextElementSibling !== node) infoCursor.after(node);
            infoCursor = node;
          } else if (gallery && node.parentElement !== gallery) {
            gallery.appendChild(node);
          }
          return;
        }

        if (buyCursor && buyCursor.parentElement) {
          if (buyCursor.nextElementSibling !== node) buyCursor.after(node);
          buyCursor = node;
        } else if (infoColumn && node.parentElement !== infoColumn) {
          infoColumn.appendChild(node);
        }
      });
    }

    /**
     * Puts the badge block where its settings say it belongs at this width:
     * inside the gallery to sit over the media, or back where it rendered to flow
     * with the buy box. Desktop and mobile are separate settings, so this is
     * re-evaluated when the viewport crosses the breakpoint.
     *
     * It can't be done in Liquid: the gallery is rendered before any block is
     * read, so the section cannot know whether this block exists or what it was
     * set to. It can't be done in CSS either — a sticky info column is a
     * containing block for absolute descendants, so pills anchored from the buy
     * box end up over the price, and no stylesheet can move a node between two
     * parents.
     */
    placeBadges() {
      const badges = this.querySelector('.pdp-badges-block[data-placement]');
      if (!badges) return;

      // The Shopify wrapper travels too, so the theme editor keeps its handle on
      // the block.
      const node = badges.closest('.shopify-block') || badges;

      // Where it rendered, remembered on the first pass so it can go back.
      if (!this.badgeHome) {
        this.badgeHome = { parent: node.parentElement, next: node.nextElementSibling };
      }

      const wanted = MOBILE.matches ? badges.dataset.placementMobile : badges.dataset.placement;
      const gallery = this.gallery;

      if (wanted === 'over_media' && gallery) {
        if (node.parentElement !== gallery) gallery.prepend(node);
        return;
      }

      const home = this.badgeHome;
      if (!home.parent || node.parentElement === home.parent) return;

      // The remembered sibling can have been replaced by a variant change, in
      // which case appending is the honest answer.
      if (home.next && home.next.parentElement === home.parent) {
        home.parent.insertBefore(node, home.next);
      } else {
        home.parent.appendChild(node);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      MOBILE.removeEventListener('change', this.placeBadges);
      if (this.syncStickyColumn) window.removeEventListener('resize', this.syncStickyColumn);
      if (this.columnObserver) this.columnObserver.disconnect();
      if (this.controller) this.controller.abort();
    }

    get productUrl() {
      return this.dataset.productUrl;
    }

    get gallery() {
      return this.querySelector('[data-pdp-gallery]');
    }

    get errorRegion() {
      return this.querySelector('[data-pdp-error]');
    }

    /**
     * What is left in the info column: the base-colour filter and the pickup
     * panel. The info links block used to open its own dialogs from here — colour
     * chart, specification sheet, video — and all three now go to
     * `product-lightbox` instead, so that branch is gone rather than dormant.
     */
    onClick(event) {
      const colorGroup = event.target.closest('[data-pdp-color-group]');
      if (colorGroup) {
        this.filterColorGroup(colorGroup);
        return;
      }

      const panel = this.querySelector('[data-pdp-pickup-panel]');
      if (!panel) return;

      if (event.target.closest('[data-pdp-pickup-open]')) {
        if (typeof panel.showModal === 'function') panel.showModal();
        return;
      }

      if (event.target.closest('[data-pdp-pickup-close]') && panel.open) panel.close();
    }

    /**
     * Base color is presentation only. Variant availability and selection still
     * come entirely from Shopify's freshly rendered option-value markup.
     */
    filterColorGroup(trigger) {
      const fieldset = trigger.closest('[data-pdp-option]');
      if (!fieldset) return;
      this.applyColorGroup(fieldset, trigger.dataset.pdpColorGroup || 'all');
    }

    applyColorGroup(fieldset, group) {
      fieldset.querySelectorAll('[data-pdp-color-group]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.pdpColorGroup === group));
      });

      fieldset.querySelectorAll('.pdp-option__item').forEach((item) => {
        const families = (item.dataset.pdpColorFamilies || '').split(/\s+/).filter(Boolean);
        item.hidden = group !== 'all' && !families.includes(group);
      });

      fieldset.querySelector('variant-swatch-carousel')?.refresh(true);
    }

    captureColorGroups() {
      const groups = new Map();
      this.querySelectorAll('[data-pdp-option]').forEach((fieldset) => {
        const active = fieldset.querySelector('[data-pdp-color-group][aria-pressed="true"]');
        if (active) groups.set(fieldset.dataset.optionPosition, active.dataset.pdpColorGroup);
      });
      return groups;
    }

    restoreColorGroups(groups) {
      groups.forEach((group, position) => {
        const fieldset = this.querySelector(`[data-pdp-option][data-option-position="${position}"]`);
        const trigger = fieldset && fieldset.querySelector(`[data-pdp-color-group="${group}"]`);
        if (fieldset && trigger) this.applyColorGroup(fieldset, group);
      });
    }

    captureSwatchCarouselPositions() {
      return Array.from(this.querySelectorAll('variant-swatch-carousel')).map(
        (carousel) => carousel.scrollPosition
      );
    }

    restoreSwatchCarouselPositions(positions) {
      this.querySelectorAll('variant-swatch-carousel').forEach((carousel, index) => {
        carousel.restorePosition(positions[index]);
      });
    }

    onChange(event) {
      const input = event.target.closest('[data-pdp-option-input]');
      if (!input) return;

      // A value belonging to a sibling product of a combined listing means the
      // whole product changes, not just the selection.
      const siblingUrl = this.siblingUrlFrom(input);
      if (siblingUrl) {
        this.switchProduct(siblingUrl, input);
        return;
      }

      this.refresh(input);
    }

    siblingUrlFrom(input) {
      if (input.tagName === 'SELECT') {
        const option = input.options[input.selectedIndex];
        return option ? option.dataset.productUrl : null;
      }
      return input.dataset.productUrl || null;
    }

    /**
     * Reads the current selection straight out of the DOM. Returns null when an
     * option has nothing selected, together with that option's name, so the
     * caller can ask for it instead of firing a pointless request.
     */
    readSelection() {
      const ids = [];
      let missing = null;

      this.querySelectorAll('[data-pdp-option]').forEach((fieldset) => {
        if (missing) return;

        const radio = fieldset.querySelector('input[type="radio"]:checked');
        if (radio) {
          ids.push(radio.dataset.optionValueId);
          return;
        }

        const select = fieldset.querySelector('select');
        const option = select ? select.options[select.selectedIndex] : null;
        if (option && option.dataset.optionValueId) {
          ids.push(option.dataset.optionValueId);
          return;
        }

        const legend = fieldset.querySelector('.pdp-option__name');
        missing = legend ? legend.textContent.trim() : '';
      });

      return { ids, missing };
    }

    async refresh(changedInput) {
      const { ids, missing } = this.readSelection();

      if (missing !== null) {
        this.showError(this.selectOptionText(missing));
        return;
      }

      const url = this.buildUrl(this.productUrl, ids);
      const markup = await this.fetchSection(url);
      if (!markup) return;

      this.applyParts(markup, changedInput);
      this.placePurchaseActions();
      this.placeServiceHighlights();
      this.syncGallery();
      this.writeUrl();
    }

    /** Combined listings: a different product, fetched and swapped whole. */
    async switchProduct(siblingUrl, changedInput) {
      const optionPosition = changedInput.dataset.optionPosition;
      const colorGroups = this.captureColorGroups();

      // No option_values: the old product's ids mean nothing here, and Shopify
      // resolves the sibling's own default selection.
      const markup = await this.fetchSection(siblingUrl);
      if (!markup) return;

      const incoming = markup.querySelector('[data-pdp]');
      if (!incoming) return;

      this.dataset.productUrl = incoming.dataset.productUrl;
      this.dataset.productId = incoming.dataset.productId;
      this.innerHTML = incoming.innerHTML;

      // Fresh markup: the remembered home points at nodes that no longer exist.
      this.badgeHome = null;
      this.placeBadges();
      this.placeUnderGallery();
      this.placePurchaseActions();
      this.placeServiceHighlights();
      this.syncGallery();
      this.restoreColorGroups(colorGroups);

      if (this.dataset.updateUrl === 'true') {
        const target = new URL(incoming.dataset.productUrl, window.location.origin);
        window.history.pushState({}, '', target.toString());
      }

      this.restoreFocusByPosition(optionPosition);
    }

    buildUrl(path, optionValueIds) {
      const url = new URL(path, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);
      if (optionValueIds && optionValueIds.length) {
        url.searchParams.set('option_values', optionValueIds.join(','));
      }
      return url.toString();
    }

    /**
     * One request in flight at a time. The abort plus the sequence check means a
     * shopper clicking through five colours can't have the second response land
     * after the fifth and win.
     */
    async fetchSection(url) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const id = ++this.requestId;
      this.setLoading(true);

      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(String(response.status));

        const text = await response.text();
        if (id !== this.requestId) return null;

        this.clearError();
        return new DOMParser().parseFromString(text, 'text/html');
      } catch (error) {
        if (error.name === 'AbortError') return null;
        // Leave the markup exactly as it was: a stale-but-valid page beats a
        // half-updated one.
        this.showError(this.dataset.textError);
        return null;
      } finally {
        if (id === this.requestId) this.setLoading(false);
      }
    }

    /**
     * Swaps the parts that depend on the selection, index-wise, so a page
     * holding more than one form (a sticky bar, say) updates all of them rather
     * than just the first.
     */
    applyParts(markup, changedInput) {
      const focusKey = this.captureFocus(changedInput);
      const colorGroups = this.captureColorGroups();
      const carouselPositions = this.captureSwatchCarouselPositions();
      const quantity = this.querySelector('[name="quantity"]');
      const quantityValue = quantity ? quantity.value : null;

      PARTS.forEach((selector) => {
        const incoming = markup.querySelectorAll(selector);
        const current = this.querySelectorAll(selector);

        current.forEach((node, index) => {
          const replacement = incoming[index];
          if (replacement) node.replaceWith(replacement.cloneNode(true));
        });
      });

      this.restoreQuantity(quantityValue);
      this.restoreColorGroups(colorGroups);
      this.restoreSwatchCarouselPositions(carouselPositions);
      this.restoreFocus(focusKey);
      this.announceVariant();
    }

    /**
     * Tells the rest of the page which variant is now selected.
     *
     * The Section Rendering API only refreshes what is inside this element, so
     * anything in another section — the tabs section's enquiry form, for one — has
     * no way to know a shopper switched colour. This is the documented seam for
     * that: read from the freshly swapped buy block rather than from a variant list,
     * so it never reaches for the variant array the theme is built to avoid.
     */
    announceVariant() {
      const buy = this.querySelector('[data-pdp-buy]');
      if (!buy) return;

      document.dispatchEvent(
        new CustomEvent('pdp:variant', {
          detail: {
            productId: this.dataset.productId,
            variantId: buy.dataset.variantId || null,
            sku: buy.dataset.variantSku || null,
            variantTitle: buy.dataset.variantTitle || null
          }
        })
      );
    }

    captureFocus(changedInput) {
      const active = document.activeElement;
      const target = this.contains(active) ? active : changedInput;
      if (!target) return null;

      return {
        optionValueId: target.dataset ? target.dataset.optionValueId : null,
        position: target.dataset ? target.dataset.optionPosition : null
      };
    }

    restoreFocus(key) {
      if (!key) return;

      if (key.optionValueId) {
        const input = this.querySelector(`[data-option-value-id="${key.optionValueId}"]`);
        if (input) {
          const focusable = input.tagName === 'OPTION' ? input.closest('select') : input;
          if (focusable) {
            focusable.focus({ preventScroll: true });
            return;
          }
        }
      }

      this.restoreFocusByPosition(key.position);
    }

    restoreFocusByPosition(position) {
      if (!position) return;
      const fieldset = this.querySelector(`[data-pdp-option][data-option-position="${position}"]`);
      if (!fieldset) return;

      const input =
        fieldset.querySelector('input[type="radio"]:checked') || fieldset.querySelector('select');
      if (input) input.focus({ preventScroll: true });
    }

    /** A quantity the shopper typed shouldn't reset because they changed colour. */
    restoreQuantity(value) {
      if (!value) return;
      const input = this.querySelector('[name="quantity"]');
      if (!input) return;

      const wanted = parseInt(value, 10);
      const min = parseInt(input.min, 10) || 1;
      const max = input.max ? parseInt(input.max, 10) : null;
      if (Number.isNaN(wanted) || wanted < min) return;
      if (max !== null && wanted > max) return;

      input.value = wanted;
    }

    syncGallery() {
      const buy = this.querySelector('[data-pdp-buy]');
      const gallery = this.gallery;
      if (!buy || !gallery) return;

      const mediaId = buy.dataset.mediaId;
      gallery.syncVariantMedia(mediaId);
    }

    writeUrl() {
      if (this.dataset.updateUrl !== 'true') return;

      const buy = this.querySelector('[data-pdp-buy]');
      if (!buy) return;

      const url = new URL(window.location.href);
      if (buy.dataset.variantId) {
        url.searchParams.set('variant', buy.dataset.variantId);
      } else {
        // No variant behind this combination — a ?variant= left over from the
        // previous selection would be a lie.
        url.searchParams.delete('variant');
      }

      // replaceState, not pushState: option changes are one visit, so Back
      // should leave the product rather than walk every colour tried.
      window.history.replaceState({}, '', url.toString());
    }

    /** Back/forward: the URL is the source of truth, so re-render from it. */
    async onPopState() {
      const productPath = new URL(this.productUrl, window.location.origin).pathname;
      const changedProduct = window.location.pathname !== productPath;

      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.sectionId);

      const markup = await this.fetchSection(url.toString());
      if (!markup) return;

      if (changedProduct) {
        const incoming = markup.querySelector('[data-pdp]');
        if (!incoming) return;
        this.dataset.productUrl = incoming.dataset.productUrl;
        this.dataset.productId = incoming.dataset.productId;
        this.innerHTML = incoming.innerHTML;
        this.badgeHome = null;
        this.placeBadges();
        this.placeUnderGallery();
        this.placePurchaseActions();
        this.placeServiceHighlights();
      } else {
        this.applyParts(markup, null);
        this.placePurchaseActions();
        this.placeServiceHighlights();
      }

      this.syncGallery();
    }

    setLoading(state) {
      if (state) {
        this.setAttribute('aria-busy', 'true');
        this.classList.add('pdp--loading');
      } else {
        this.removeAttribute('aria-busy');
        this.classList.remove('pdp--loading');
      }
    }

    selectOptionText(optionName) {
      const template = this.dataset.textSelectOption || '';
      return template.replace('__option__', optionName);
    }

    showError(message) {
      const region = this.errorRegion;
      if (region && message) region.textContent = message;
    }

    clearError() {
      const region = this.errorRegion;
      if (region && region.textContent) region.textContent = '';
    }
  }

  if (!customElements.get('product-gallery')) {
    customElements.define('product-gallery', ProductGallery);
  }

  if (!customElements.get('variant-swatch-carousel')) {
    customElements.define('variant-swatch-carousel', VariantSwatchCarousel);
  }

  if (!customElements.get('product-lightbox')) {
    customElements.define('product-lightbox', ProductLightbox);
  }

  if (!customElements.get('product-info')) {
    customElements.define('product-info', ProductInfo);
  }
})();
